import { useMemo } from 'react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { compose, groupBy, sort } from 'ramda'

import {
  useLeaseContractsDebitNotesList,
  LeaseContractsDebitNotesListWithDate
} from 'services/contracts/hooks/useLeaseContractsDebitNotesList'
import {
  useLeaseContractsPaymentSchedulesList,
  LeaseContractsPaymentSchedulesListWithTime
} from 'services/contracts/hooks/useLeaseContractsPaymentSchedulesList'
import {
  useLeaseContractsInvoicesList,
  LeaseContractsInvoicesListWithDate
} from 'services/contracts/hooks/useLeaseContractsInvoicesList'
import {
  useLeaseContractsInterestNotesList,
  LeaseContractsInterestNotesListWithDate
} from 'services/contracts/hooks/useLeaseContractsInterestNotesList'
import { useLanguage } from 'services/i18n/i18n.hooks'

dayjs.extend(isBetween)

type SchedulesWithDocumnetsType = LeaseContractsDebitNotesListWithDate &
  LeaseContractsPaymentSchedulesListWithTime &
  LeaseContractsInvoicesListWithDate &
  LeaseContractsInterestNotesListWithDate

export const useSchedulesWithDocumnets = ({
  activeLeaseContractId,
  isPaid
}: {
  activeLeaseContractId: number
  isPaid: boolean
}) => {
  const { dateAdapter } = useLanguage()

  const { leaseContractsInvoicesList, isLoading: isLoadingLeaseContractsInvoicesList } =
    useLeaseContractsInvoicesList(activeLeaseContractId, isPaid)
  const { leaseContractsDebitNotesLis, isLoading: isLoadingLeaseContractsDebitNotesList } =
    useLeaseContractsDebitNotesList(activeLeaseContractId, isPaid)
  const { leaseContractsInterestNotesList, isLoading: isLoadingLeaseContractsInterestNotesList } =
    useLeaseContractsInterestNotesList(activeLeaseContractId, isPaid)
  const { leaseContractsPaymentSchedulesList } =
    useLeaseContractsPaymentSchedulesList(activeLeaseContractId)

  // Wszystkie faktury (opłacone i nieopłacone) – potrzebne do wykrycia opłaconych faktur
  // z nieopłaconą korektą. `isPaid` celowo pominięte, żeby backend nie filtrował listy.
  const { leaseContractsInvoicesList: allInvoicesList } =
    useLeaseContractsInvoicesList(activeLeaseContractId)

  // Zidentyfikuj faktury, które mają korekty
  const invoicesWithCorrections = useMemo(() => {
    // Identyfikuj faktury, które są korektami (mają parent)
    const correctionInvoices = allInvoicesList.filter(invoice => invoice.parent !== undefined);
    
    // Zbierz ID oryginalnych faktur, które mają korekty
    return correctionInvoices.map(invoice => ({
      originalId: invoice.parent?.id,
      correctionId: invoice.id
    }));
  }, [allInvoicesList]);

  // Funkcja pomocnicza do używania daty oryginalnej faktury dla korekt
  const processInvoicesList = useMemo(() => {
    return leaseContractsInvoicesList.map(invoice => {
      // Jeśli to jest korekta (ma rodzica)
      if (invoice.parent) {
        // Użyj daty z oryginalnej faktury
        return {
          ...invoice,
          displayDate: dayjs(invoice.parent.dateOfIssue).startOf('month'),
          // Zachowaj pozostałe dane
        };
      }
      return invoice;
    });
  }, [leaseContractsInvoicesList]);

  // Sprawdź, czy faktury opłacone powinny być przeniesione do "Do zapłaty" ze względu na korekty
  const adjustedInvoicesList = useMemo(() => {
    if (!isPaid) {
      // W zakładce "Do zapłaty" dodajemy faktury, które są opłacone, ale mają korekty
      const invoicesWithNonPaidCorrections = allInvoicesList
        .filter(invoice => invoice.isPaid && // tylko opłacone faktury
          invoicesWithCorrections.some(correction => 
            correction.originalId === invoice.id && // które mają korekty
            !allInvoicesList.find(inv => inv.id === correction.correctionId)?.isPaid // i korekta nie jest opłacona
          )
        );
      
      return [...processInvoicesList, ...invoicesWithNonPaidCorrections];
    }
    
    // W zakładce "Opłacone" pokazujemy tylko te faktury, które są w pełni opłacone i nie mają nieopłaconych korekt
    return processInvoicesList.filter(invoice => {
      // Sprawdź czy ta faktura ma nieopłaconą korektę
      const hasNonPaidCorrection = invoicesWithCorrections.some(correction => 
        correction.originalId === invoice.id && 
        !allInvoicesList.find(inv => inv.id === correction.correctionId)?.isPaid
      );
      
      // Wyświetl tylko te, które nie mają nieopłaconych korekt
      return !hasNonPaidCorrection;
    });
  }, [isPaid, processInvoicesList, allInvoicesList, invoicesWithCorrections]);

  const schedulesWithDocumnets = useMemo<{
    [key: string]: SchedulesWithDocumnetsType[]
  }>(
    () =>
      compose(
        groupBy(({ displayDate }: SchedulesWithDocumnetsType) =>
          displayDate.locale(dateAdapter).format('MMMM YYYY')
        ),
        sort((a, b) => (dayjs(a.displayDate).isAfter(dayjs(b.displayDate)) ? 1 : -1))
      )(
        isPaid
          ? ([
              ...adjustedInvoicesList,
              ...leaseContractsDebitNotesLis,
              ...leaseContractsInterestNotesList
            ] as any)
          : ([
              ...adjustedInvoicesList,
              ...leaseContractsDebitNotesLis,
              ...leaseContractsInterestNotesList,
              ...leaseContractsPaymentSchedulesList
            ] as any)
      ) as any,
    [
      adjustedInvoicesList,
      leaseContractsDebitNotesLis,
      leaseContractsInterestNotesList,
      leaseContractsPaymentSchedulesList,
      isPaid,
      dateAdapter
    ]
  )

  // Saldo konta NIE jest liczone po stronie klienta z dokumentów – rozjeżdżało się z CRM
  // przy nadpłatach i niedopiętych wpłatach. Źródłem salda jest
  // useLeaseContractsRentAccountBalance (/lease/contracts/{id}/rent-account-balance/).

  return {
    schedulesWithDocumnets,
    isLoading:
      isLoadingLeaseContractsInterestNotesList ||
      isLoadingLeaseContractsDebitNotesList ||
      isLoadingLeaseContractsInvoicesList
  }
}
